import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import {
    ActionGroup,
    ActionItem,
    BaseContainer,
    BatchObject,
    JobStatus,
    PaginatedBatch,
    RequestPaginationInfo
} from '@stylehaven/components';
import { BehaviorSubject, combineLatest, EMPTY, Observable, timer } from 'rxjs';
import { catchError, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { MoreActionsButton } from '../../../entities/common/more-actions.helper';
import { PaginatedResults } from '../../../record/entities/pagination/paginated-results.entity';
import { defaultPaginationInfo, PaginationInfo } from '../../../record/entities/pagination/pagination-info.entity';
import { BatchesSandboxService } from '../../sandbox/batches-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-batches-overview',
    templateUrl: './batches-overview-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BatchesOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private readonly POLLING_INTERVAL: number = 5000;

    readonly COLUMN_COUNT: number = 9;
    readonly currentPaginationInfo$ = new BehaviorSubject<PaginationInfo>(defaultPaginationInfo);
    actionItems$: Observable<ActionGroup[]>;

    pagedResult$!: Observable<PaginatedResults<PaginatedBatch>>;

    batches$!: Observable<PaginatedBatch[]>;
    paginationInfo$!: Observable<PaginationInfo>;

    tableHeaderWithCount$!: Observable<string>;

    constructor(private readonly sandbox: BatchesSandboxService) {
        super();
    }

    ngOnInit(): void {
        this.initSourceStreams();

        this.initTableHeaderStream();
        this.initActionItemStream();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.currentPaginationInfo$.complete();
    }

    private initTableHeaderStream() {
        this.tableHeaderWithCount$ = this.paginationInfo$.pipe(
            map(paginationInfo => this.getTableHeaderTitle(paginationInfo))
        );
    }

    private getTableHeaderTitle(paginationInfo: PaginationInfo) {
        return this.translateService.instant('batches.results', { items: paginationInfo.totalNumberOfResults });
    }

    protected initActionItemStream(): void {
        this.actionItems$ = this.pagedResult$.pipe(
            switchMap(paginatedResults =>
                combineLatest(paginatedResults.results.map(result => this.getActionItems(result)))
            ),
            startWith([]),
            shareReplay(1)
        );
    }

    private getActionItems(batch: PaginatedBatch): Observable<ActionGroup> {
        const actionItems: ActionItem = {
            label: this.translateService.instant('batches.overview.error'),
            eventListener: () => this.sandbox.openErrorDialog(batch)
        };

        return batch.Status$.pipe(
            map(status => ({
                actions:
                    status === JobStatus.FAILED || status === JobStatus.INTERRUPTED
                        ? [MoreActionsButton([actionItems])]
                        : []
            }))
        );
    }

    trackByBatchId(index: number, item: BatchObject): number {
        return index;
    }

    paginationInfoChanged(output: RequestPaginationInfo) {
        this.currentPaginationInfo$.next({ ...this.currentPaginationInfo$.value, ...output });
    }

    getSkippedInfoMsg(batch: BatchObject): string {
        // No type check, because taskType is not returned correctly due to engine bug related to custom dav batch types :(
        // Ok for now since we only show one batch type
        if (batch.Skipped > 0) {
            return this.translateService.instant('batches.remark.BatchSubmitDossiersTask');
        }
        return '-';
    }

    private initSourceStreams(): void {
        this.pagedResult$ = this.currentPaginationInfo$.pipe(
            tap(() => this.startLoading()),
            switchMap(paginationInfo => this.getBatchesWithPolling(paginationInfo)),
            tap(() => this.stopLoading()),
            shareReplay(1)
        );

        this.paginationInfo$ = this.pagedResult$.pipe(
            map(pagedResult => pagedResult.paginationInfo),
            shareReplay(1)
        );

        this.batches$ = this.pagedResult$.pipe(
            map(pagedResult => pagedResult.results),
            shareReplay(1)
        );
    }

    private getBatchesWithPolling(paginationInfo: PaginationInfo) {
        return timer(0, this.POLLING_INTERVAL).pipe(
            switchMap(() => this.getBatches(paginationInfo)),
            catchError(() => this.onGetBatchesError())
        );
    }

    private getBatches(paginationInfo: PaginationInfo): Observable<PaginatedResults<BatchObject>> {
        return this.sandbox.getBatchList(paginationInfo);
    }

    private startLoading(): void {
        this.setError(false);
        this.setLoading(true);
    }

    private stopLoading(): void {
        this.setError(false);
        this.setLoading(false);
    }

    private onGetBatchesError(): Observable<any> {
        this.setLoading(false);
        this.setError(true);

        return EMPTY;
    }
}
